import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * PageHeader component - Page header (breadcrumbs + title + description + action button)
 * @param {array} breadcrumbs - Breadcrumb data array, e.g. [{ label: 'Dashboard', path: '/' }, { label: 'Contract Review' }]
 * @param {string} title - Page title
 * @param {string} icon - Page icon emoji
 * @param {string} description - Page description
 * @param {object} actionButton - Top right action button (optional), e.g. { icon: '✨', label: 'New Conversation', onClick: handleClick }
 * @param {React.ReactNode} children - Additional content (optional)
 */
const PageHeader = ({ 
  breadcrumbs = [], 
  title,
  icon,
  description,
  actionButton = null,
  children
}) => {
  return (
    <div 
      className="rounded-2xl px-8 py-6 mb-6 transition-colors duration-300"
      style={{ 
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)'
      }}
    >
      {/* Breadcrumb navigation + action button */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Breadcrumb navigation */}
          {breadcrumbs.length > 0 && (
            <nav className="flex items-center space-x-2 text-sm mb-4">
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                  )}
                  {item.path && index < breadcrumbs.length - 1 ? (
                    <Link
                      to={item.path}
                      className="font-medium transition-colors"
                      style={{ color: 'var(--color-primary)' }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span 
                      className="font-medium"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {item.label}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}

          {/* Page title and icon */}
          {(title || icon) && (
            <div className="flex items-center">
              {icon && (
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mr-4"
                  style={{ backgroundColor: 'var(--color-primary-light)' }}
                >
                  <span className="text-2xl">{icon}</span>
                </div>
              )}
              {title && (
                <h1 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--color-text)' }}
                >
                  {title}
                </h1>
              )}
            </div>
          )}

          {/* Page description */}
          {description && (
            <p 
              className="mt-3 max-w-2xl"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {description}
            </p>
          )}
        </div>

        {/* Top right action button */}
        {actionButton && (
          <button
            onClick={actionButton.onClick}
            className="flex items-center px-5 py-2.5 text-white font-medium rounded-lg transition-colors shadow-sm hover:opacity-90"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {actionButton.icon && (
              <span className="mr-2">{actionButton.icon}</span>
            )}
            {actionButton.label}
          </button>
        )}
      </div>

      {/* Additional content */}
      {children && (
        <div className="mt-4">
          {children}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
